import React, { useContext, useEffect, useRef, useState } from 'react';
import { Bell, RefreshCw } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import PushSubscribeButton from '../../components/pushSubscribeButton/PushSubscribeButton';
import { gallery } from './bohoFloralsContent';

function OwnerContent({ token, requestId }) {
  const [state, setState] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [device, setDevice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(true);
  const busy = useRef(false);
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  useEffect(() => {
    let current = true;
    mounted.current = true;
    setState(null);
    (async () => {
      try {
        const { data } = await axiosConn.get('boho-florals/owner-alerts', { headers: { Authorization: `Bearer ${token}` } });
        if (!current) return;
        if (data?.data?.authorized !== true) throw new Error('unverified');
        const path = requestId ? `boho-florals/requests/${encodeURIComponent(requestId)}` : 'boho-florals/requests';
        const response = await axiosConn.get(path, { headers: { Authorization: `Bearer ${token}` } });
        if (current) setState({ ...data.data, requests: requestId ? [response.data.data] : response.data.data });
      } catch (err) {
        if (current) setState({ error: err.response?.status === 403 ? 'This account is not authorized to handle Boho orders.'
          : err.response?.status === 401 ? 'Your session has expired. Sign in again.'
          : err.response?.status === 404 ? 'This request is unavailable.' : 'Owner access or requests could not be verified. Please retry.' });
      }
    })();
    return () => { current = false; mounted.current = false; };
  }, [token, requestId, attempt]);
  const save = async enabled => {
    if (busy.current) return;
    busy.current = true; setSaving(true); setError('');
    try {
      const { data } = await axiosConn.patch('boho-florals/owner-alerts', { enabled }, headers);
      if (data?.data?.authorized !== true || data.data.enabled !== enabled) throw new Error('Invalid preference');
      if (mounted.current) setState(previous => ({ ...previous, ...data.data }));
    } catch (_) { if (mounted.current) setError('The setting was not saved. Please retry.'); }
    finally { busy.current = false; if (mounted.current) setSaving(false); }
  };
  const returnTo = `/BohoFlorals?${requestId ? `request=${encodeURIComponent(requestId)}` : 'ownerAlerts=1'}#bf-owner-alerts`;
  if (!state) return <p role="status">Checking owner access...</p>;
  if (state.error) return <><p role="alert">{state.error}</p><a href={`/login?returnTo=${encodeURIComponent(returnTo)}`}>Sign in again</a><button type="button" className="bf-button" onClick={() => setAttempt(value => value + 1)}><RefreshCw size={18} aria-hidden="true" />Retry</button></>;
  return <>
    <p>Phone and browser notifications. No SMS or email.</p>
    <PushSubscribeButton jwt={token} vapidKey={process.env.REACT_APP_VAPID_PUBLIC_KEY}
      onStateChange={setDevice} onSubscribed={() => save(true)}
      copy={{ enableLabel: 'Enable owner alerts', helpText: 'Enable notifications on this device.' }} />
    <label className="bf-owner-alert-toggle"><input type="checkbox" checked={state.enabled === true}
      disabled={saving || (!state.enabled && !device?.subscribed)} onChange={event => save(event.target.checked)} />Order request alerts</label>
    {error && <p role="alert">{error}</p>}
    {(!state.workflowConnected || !state.deliveryEnabled) && <p className="bf-owner-alerts-pending" role="status">Live requests or alert delivery are not enabled yet. Device setup does not activate them.</p>}
    <div className="bf-owner-requests">
      <h3>{requestId ? 'Order request' : 'Recent requests'}</h3>
      {requestId && <a href="/BohoFlorals?ownerAlerts=1#bf-owner-alerts">All recent requests</a>}
      {!Array.isArray(state.requests) ? <p role="alert">Requests could not be loaded.</p> : state.requests.length === 0 ? <p>No requests yet.</p> : state.requests.map(request => {
        const inspiration = gallery.find(photo => photo.id === request.inspirationId);
        return <article key={request.id} className="bf-owner-request">
          <h4>{request.name}</h4><p>{request.occasion}</p>
          {!requestId ? <a href={`/BohoFlorals?request=${request.id}#bf-owner-alerts`}>Review request</a> : <>
            <dl><dt>Contact</dt><dd>{request.contact}</dd><dt>Fulfillment</dt><dd>{request.fulfillment}{request.area && ` - ${request.area}`}</dd><dt>Requested date</dt><dd>{request.date || 'Not specified'}</dd><dt>Budget</dt><dd>{request.budget || 'Not specified'}</dd></dl>
            {request.message && <p className="bf-owner-request-message">{request.message}</p>}
            {inspiration && <figure><img src={inspiration.src} alt={inspiration.alt} /><figcaption>{inspiration.title} (sample inspiration)</figcaption><a href={inspiration.src} target="_blank" rel="noopener noreferrer">Open inspiration image</a></figure>}
            <p>Request only. Confirm availability and arrangements with the customer.</p>
          </>}
        </article>;
      })}
    </div>
  </>;
}

export default function BohoOwnerAlerts({ requestId = '' }) {
  const { player } = useContext(AuthContext) || {};
  const token = player?.token;
  const returnTo = `/BohoFlorals?${requestId ? `request=${encodeURIComponent(requestId)}` : 'ownerAlerts=1'}#bf-owner-alerts`;
  return <section id="bf-owner-alerts" className="bf-section bf-owner-alerts" aria-labelledby="bf-owner-alerts-title">
    <h2 id="bf-owner-alerts-title"><Bell size={24} aria-hidden="true" />Owner alerts</h2>
    {!token ? <p><a href={`/login?returnTo=${encodeURIComponent(returnTo)}`}>Sign in to JoeGalaxy</a> with the authorized Boho order handler account.</p>
      : <OwnerContent key={`${token}:${requestId}`} token={token} requestId={requestId} />}
  </section>;
}
